import React, { useState, useEffect } from "react";
import styled from "styled-components";
import client from "../sanityClient";
import hammerAlesGraphic from "../assets/images/graphics/hammer-flyer.webp";
import beerBackground from "../assets/video/beer.mp4";

// Styled Components
const Container = styled.div`
  position: relative;
  color: #fff;
  text-align: center;
  overflow: hidden;
  min-height: 50vh;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  background: url("/img/intro-bg.png") center center no-repeat;
  background-size: cover;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: -1;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const ImageContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const StyledImage = styled.img`
  max-width: 80%;
  border-radius: 10px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TextContent = styled.div`
  flex: 1;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0;
    text-align: center;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 15px;
  color: #fff;
`;

const Description = styled.p`
  font-size: 1rem;
  color: #ddd;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const RegisterButton = styled.a`
  display: inline-block;
  background-color: #007bff;
  color: #fff;
  padding: 12px 24px;
  border-radius: 5px;
  font-size: 1.1rem;
  font-weight: bold;
  text-decoration: none;
  margin-bottom: 20px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const ProgressContainer = styled.div`
  margin: 0 auto;
  max-width: 300px;
  text-align: center;
`;

const ProgressTitle = styled.h3`
  color: #fff;
  margin-bottom: 10px;
  font-size: 16px;
`;

const ProgressBar = styled.div`
  background: #ccc;
  border-radius: 10px;
  overflow: hidden;
  height: 20px;
  margin-bottom: 10px;
`;

const ProgressBarFill = styled.div`
  background: #ff9800;
  height: 100%;
  transition: width 1.5s ease;
`;

const ProgressText = styled.p`
  font-size: 14px;
  color: #ddd;
`;

const LearnMoreButton = styled.a`
  display: inline-block;
  background-color: transparent;
  color: #fff;
  border: 2px solid #fff;
  padding: 12px 24px;
  border-radius: 5px;
  font-size: 1.1rem;
  font-weight: bold;
  text-decoration: none;
  margin-left: 15px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #fff;
    color: #000;
  }
`;

const EventHighlight = () => {
  const [beerCardData, setBeerCardData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [useVideo, setUseVideo] = useState(false);

  const cardPrice = 40;
  const goal = 120000;

  useEffect(() => {
    client
      .fetch(
        `*[_type == "beerCardCount"][0]{
           cardsSold,
           totalCards
         }`
      )
      .then((data) => {
        if (data) {
          setBeerCardData(data);
        }
      })
      .catch((error) => {
        console.error("Error fetching beer card data from Sanity:", error);
      });

    const checkInternetSpeed = async () => {
      const startTime = new Date().getTime();
      try {
        await fetch("https://www.google.com/favicon.ico", { mode: "no-cors" });
        const endTime = new Date().getTime();
        const duration = endTime - startTime;
        if (duration < 200) {
          setUseVideo(true);
        }
      } catch (error) {
        console.error("Internet speed check failed:", error);
      }
    };

    checkInternetSpeed();
  }, []);

  useEffect(() => {
    if (beerCardData) {
      const progressPercentage =
        ((beerCardData.cardsSold * cardPrice) / goal) * 100;
      const timeout = setTimeout(() => {
        setProgress(progressPercentage);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [beerCardData]);

  if (!beerCardData) return <p>Loading...</p>;

  const { cardsSold, totalCards } = beerCardData;
  const amountRaised = cardsSold * cardPrice;

  return (
    <Container>
      <Background>
        {useVideo && (
          <video autoPlay loop muted playsInline>
            <source src={beerBackground} type="video/mp4" />
          </video>
        )}
      </Background>

      <Overlay />

      <Content>
        <ImageContent>
          <StyledImage
            src={hammerAlesGraphic}
            alt="7th Annual Hammer & Ales Graphic"
          />
        </ImageContent>
        <TextContent>
          <Title>7th Annual Hammer & Ales</Title>

          <Description>
            Join us for Habitat Buffalo's most exciting fundraiser of the year!
            Enjoy live entertainment, delicious food, local craft beverages, and
            engaging games and activities—all while supporting the House That
            Beer Built campaign. Every ticket helps us build affordable homes
            for families in need.
          </Description>
          <RegisterButton
            href="https://habitatbuffalo.betterworld.org/events/hammerandales2025"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Your Tickets Now
          </RegisterButton>
          <LearnMoreButton href="/hammerandales">Learn More</LearnMoreButton>
        </TextContent>
      </Content>
    </Container>
  );
};

export default EventHighlight;
